if (!customElements.get('tiered-pricing-tabs')) {
  customElements.define(
    'tiered-pricing-tabs',
    class TieredPricingTabs extends HTMLElement {
      constructor() {
        super();
        this.productId = this.dataset.productId;
        this.variantId = this.dataset.variantId;
        this.basePrice = parseFloat(this.dataset.basePrice);
        this.sectionId = this.dataset.sectionId;
        this.isMixAndMatch = this.dataset.mixAndMatch === 'true';
        this.tabs = this.querySelectorAll('.tiered-pricing-tab');
        this.selectedTab = this.querySelector('.tiered-pricing-tab--active');
        this.quantityChangeHandler = this.onQuantityChange.bind(this);
        
        // Set up event listeners for tabs
        this.tabs.forEach(tab => {
          tab.addEventListener('click', this.onTabClick.bind(this));
        });
        
        // Subscribe to variant change event to update tab prices
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.handleVariantChange(event);
        });
        
        // Initialize price elements cache
        this.priceElements = [];
        this.initPriceElements();
        
        // Initialize quantity input monitoring
        this.initQuantityInputListeners();
        
        // Select the highest tier by default (last tab).
        // Mix-and-match keeps the PDP quantity at 1 until a tier is chosen.
        if (this.isMixAndMatch && this.tabs.length > 0) {
          const highestTierTab = this.tabs[this.tabs.length - 1];
          this.selectTier(
            Number.parseInt(highestTierTab.dataset.quantity, 10),
            Number.parseFloat(highestTierTab.dataset.price),
            { updateQuantity: false },
          );
        } else if (this.tabs.length > 1) {
          const highestTierTab = this.tabs[this.tabs.length - 1];
          this.onTabClick({ currentTarget: highestTierTab });
        }
      }
      
      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        
        // Remove quantity input listeners when component is removed
        this.removeQuantityInputListeners();
      }
      
      initPriceElements() {
        // Find all price elements that need to be updated
        const priceSelectors = [
          `#price-${this.sectionId} .price-item--regular`,
          `#Price-Alt-${this.sectionId} .price-item--regular`,
          `.price-${this.sectionId} .price-item--regular`
        ];
        
        priceSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          if (elements.length > 0) {
            elements.forEach(element => this.priceElements.push(element));
          }
        });
      }
      
      initQuantityInputListeners() {
        // Find all quantity inputs and add change listeners
        const quantityInputs = document.querySelectorAll(`.quantity__input[id^="Quantity-"]`);
        
        if (quantityInputs.length > 0) {
          // Store references to the inputs for cleanup
          this.quantityInputs = quantityInputs;
          
          // Add change event listeners to each input
          quantityInputs.forEach(input => {
            input.addEventListener('change', this.quantityChangeHandler);
          });
        }
      }
      
      removeQuantityInputListeners() {
        if (this.quantityInputs && this.quantityInputs.length > 0) {
          this.quantityInputs.forEach(input => {
            input.removeEventListener('change', this.quantityChangeHandler);
          });
        }
      }
      
      onQuantityChange(event) {
        const input = event.target;
        const quantity = parseInt(input.value);
        
        // Find the appropriate tab for this quantity
        this.selectTabByQuantity(quantity);
        
        // Update savings badge with actual quantity value
        this.updateSavingsBadge(quantity);
      }
      
      // Set a tab as active and update UI
      setActiveTab(tab, updateQuantity = false) {
        if (tab === this.selectedTab) return;
        
        // Update selected state
        this.selectedTab.classList.remove('tiered-pricing-tab--active');
        this.selectedTab.setAttribute('aria-selected', 'false');
        tab.classList.add('tiered-pricing-tab--active');
        tab.setAttribute('aria-selected', 'true');
        this.selectedTab = tab;
        
        // Update product price
        const price = parseFloat(tab.dataset.price);
        this.updateProductPrice(price);
        
        // If needed, update quantity input as well
        if (updateQuantity) {
          const quantity = parseInt(tab.dataset.quantity);
          this.updateQuantityInput(quantity);
        }
      }

      selectTier(quantity, price, { updateQuantity = true } = {}) {
        if (!Number.isFinite(quantity) || !Number.isFinite(price)) {
          return;
        }

        const matchingTab = Array.from(this.tabs).find(
          (tab) => Number.parseInt(tab.dataset.quantity, 10) === quantity,
        );

        if (matchingTab && matchingTab !== this.selectedTab) {
          this.selectedTab.classList.remove('tiered-pricing-tab--active');
          this.selectedTab.setAttribute('aria-selected', 'false');
          matchingTab.classList.add('tiered-pricing-tab--active');
          matchingTab.setAttribute('aria-selected', 'true');
          this.selectedTab = matchingTab;
        }

        if (updateQuantity) {
          this.updateQuantityInput(quantity);
          this.updateProductPrice(price, quantity);
          return;
        }

        this.updateProductPrice(price, 1);
      }
      
      selectTabByQuantity(quantity) {
        // Get all tabs sorted by quantity (highest first) to find the appropriate tier
        const tabsByQuantity = Array.from(this.tabs).sort((a, b) => {
          return parseInt(b.dataset.quantity) - parseInt(a.dataset.quantity);
        });
        
        // Find the first tab that has a quantity less than or equal to the input quantity
        let tabToSelect = tabsByQuantity[tabsByQuantity.length - 1]; // Default to first tier (1 unit)
        
        for (const tab of tabsByQuantity) {
          const tabQuantity = parseInt(tab.dataset.quantity);
          if (tabQuantity <= quantity) {
            tabToSelect = tab;
            break;
          }
        }
        
        // Set this tab as active, but don't update quantity inputs
        this.setActiveTab(tabToSelect, false);
      }
      
      onTabClick(event) {
        const clickedTab = event.currentTarget;
        // Set this tab as active and update quantity inputs
        this.setActiveTab(clickedTab, true);
      }
      
      handleVariantChange(event) {
        const { data } = event;
        if (!data || !data.variant) return;
        
        // Update variant ID and base price
        this.variantId = data.variant.id.toString();
        this.basePrice = data.variant.price;
        
        // Update tab prices with the new base variant price
        this.updateTabForBasePrice();
        
        // If a tier is selected, update the product price
        if (this.isMixAndMatch || this.selectedTab !== this.querySelector('.tiered-pricing-tab:first-child')) {
          this.updateProductPrice(parseFloat(this.selectedTab.dataset.price));
        }
      }
      
      updateTabForBasePrice() {
        if (this.isMixAndMatch) {
          for (const tab of this.tabs) {
            const discountPercentage = Number.parseFloat(tab.dataset.discountPercentage);

            if (Number.isFinite(discountPercentage)) {
              const price = (this.basePrice * (100 - discountPercentage)) / 100;
              tab.dataset.price = price;
              const priceElement = tab.querySelector('.tiered-pricing-tab__price');
              if (priceElement) {
                priceElement.textContent = this.formatTierPrice(price);
              }
            }
          }
          return;
        }

        // Update the first tab (1 unit) with new base price
        const firstTab = this.querySelector('.tiered-pricing-tab:first-child');
        firstTab.dataset.price = this.basePrice;
        const priceElement = firstTab.querySelector('.tiered-pricing-tab__price');
        priceElement.textContent = this.formatTierPrice(this.basePrice);
      }
      
      formatTierPrice(cents) {
        return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
      }

      updateProductPrice(price, selectedQuantity = Number.parseInt(this.selectedTab.dataset.quantity, 10)) {
        if (this.isMixAndMatch) {
          this.updateSavingsBadge(selectedQuantity, price);
          return;
        }

        // Find the price container
        const priceContainer = this.closest('.product-tiered-pricing');
        var b2bFlag = false;
        var b2bPrice = this.basePrice;

        if(priceContainer.getAttribute("market-type") == "b2b") {
          b2bFlag = true;
          price = price * 0.8;
          b2bPrice = this.basePrice * 0.8;
        }

        // Update price element
        const moneyElement = priceContainer.querySelector('.price-item--regular price-money bdi, .price-item--regular bdi');
        if (moneyElement) {
          var moneyText = theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat);
          if(price >= 100000) {
            moneyText = moneyText.replace(",", ".");
          }
          moneyElement.textContent = moneyText;
        }
        
        // Update the message
        const messageContainer = priceContainer.querySelector('.tiered-price-message');
        const messageElement = priceContainer.querySelector('.tiered-price-message__text');
        const regularPrice = priceContainer.querySelector('.tiered-price-regular-price');
        
        if(b2bFlag == false) {
          if (messageContainer && messageElement) {
            var translatedText = selectedQuantity > 1 ? theme.tieredPricingStrings.greatPriceMessage.other : theme.tieredPricingStrings.greatPriceMessage.one;
            if(messageElement.classList.contains('black-friday-price-message')) {
              translatedText = theme.tieredPricingStrings.black_friday_price.replace('{{ qty }}', selectedQuantity);
            }
            messageElement.textContent = translatedText.replace('{{ count }}', selectedQuantity);
          }
        }
        // Handle the regular price / max tier price display
        if (regularPrice) {
          const regularPriceText = regularPrice.querySelector('.tiered-price-regular-price__text');
          var monneySubTxt = "";
          if(b2bFlag == true) {
              const shopUrl = "finewines-aps.myshopify.com";
              const storefrontAccessToken = "572deeb3a166e5a302911ffaf1ed1bf4";
              const productHandle = regularPriceText.closest(".fw-prodcut-price").getAttribute("product-handle");
              const graphQLQuery = `{
                product(handle: "${productHandle}") {
                  title
                  variants(first: 10) {
                    edges {
                      node {
                        id
                        title
                        price {
                            amount
                        }
                      }
                    }
                  }
                }
              }`;

            
              fetch(`https://${shopUrl}/api/graphql`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
                },
                body: JSON.stringify({ query: graphQLQuery }),
              })
              .then(response => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
              })
              .then(data => {
                var dataVariants = data["data"]["product"]["variants"]["edges"];
                const variantId = regularPriceText.closest(".fw-prodcut-price").querySelector("input[name='id']").value;
                for(var i = 0; i < dataVariants.length; i ++) {
                  const var_id = dataVariants[i]["node"]["id"].split("gid://shopify/ProductVariant/")[1];
                  const var_price = dataVariants[i]["node"]["price"]["amount"];
                  if(variantId == var_id) {
                    b2bPrice = var_price * 80;
                  }
                }

                regularPrice.style.display = 'block';
                monneySubTxt = theme.tieredPricingStrings.regular_price.replace('{{ price }}', theme.Currency.formatMoney(b2bPrice, theme.shopSettings.moneyFormat));
                if(b2bPrice >= 100000) {
                  monneySubTxt = monneySubTxt.replace(",", ".");
                }
                regularPriceText.textContent = monneySubTxt + " ekskl. moms";
              })
              .catch(error => {
                console.error("Error fetching from Storefront API:", error);
              });
          } else {
          if (selectedQuantity > 1) {
            // When a tier is selected, show the regular price
            regularPrice.style.display = 'block';
            monneySubTxt = theme.tieredPricingStrings.regular_price.replace('{{ price }}', theme.Currency.formatMoney(this.basePrice, theme.shopSettings.moneyFormat));
            if(this.basePrice >= 100000) {
              monneySubTxt = monneySubTxt.replace(",", ".");
            }
            regularPriceText.textContent = monneySubTxt;
          } else {
            // When 1 unit is selected, show the best available price
            const lastTab = this.tabs[this.tabs.length - 1];
            if (lastTab && parseInt(lastTab.dataset.quantity) > 1) {
              const maxTierPrice = parseFloat(lastTab.dataset.price);
              const maxTierQuantity = parseInt(lastTab.dataset.quantity);
              regularPrice.style.display = 'block';
              monneySubTxt = theme.tieredPricingStrings.max_tier_price
                .replace('{{ price }}', theme.Currency.formatMoney(maxTierPrice, theme.shopSettings.moneyFormat))
                .replace('{{ count }}', maxTierQuantity);
              if(maxTierPrice >= 100000) {
                monneySubTxt = monneySubTxt.replace(",", ".");
              }
              regularPriceText.textContent = monneySubTxt;

            } else {
              regularPrice.style.display = 'none';
            }
          }
          }
        }
      
        // Update savings badge with the selected tab quantity
        this.updateSavingsBadge(selectedQuantity, price);
        
        // Publish price change for other components
        publish(PUB_SUB_EVENTS.priceUpdate, {
          price: price,
          variantId: this.variantId
        });
      }
      
      updateSavingsBadge(quantity, price = null) {
        // Find the price container
        const priceContainer = this.closest('.product-tiered-pricing');
        const savingsBadge = priceContainer.querySelector('.tiered-savings-badge');
        
        if (!savingsBadge) return;
        
        // If no price is provided, use the selected tab's price
        if (price === null) {
          price = parseFloat(this.selectedTab.dataset.price);
        }
        
        if (quantity > 1) {
          // Calculate savings (base price minus tier price) * actual quantity
          const savings = (this.basePrice - price) * quantity;
          
          if (savings > 0) {
            savingsBadge.style.display = '';
            // Use the translated string with variable replacement
            const translatedText = theme.tieredPricingStrings.savingsBadge;
            const newtext = translatedText.replace('{{ amount }}', theme.Currency.formatMoney(savings, theme.shopSettings.moneyFormat)).replace('{{ count }}', quantity);
            savingsBadge.textContent = savings >= 100000 ? newtext.replace(",", ".") : newtext;
          } else {
            savingsBadge.style.display = 'none';
          }
        } else {
          savingsBadge.style.display = 'none';
        }
      }
      
      updateQuantityInput(quantity) {
        // Temporarily remove our quantity change listeners to avoid circular updates
        this.removeQuantityInputListeners();
        
        // Find all quantity inputs on the page
        // This will match both primary and sticky inputs
        const quantityInputs = document.querySelectorAll(`.quantity__input[id^="Quantity-"]`);
        
        if (quantityInputs.length > 0) {
          quantityInputs.forEach(input => {
            // Always update the quantity value when a tier is selected
            input.value = quantity;
            
            // Create and dispatch a change event to notify other components
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
            
            // If this input is part of a quantity-input component, also notify it directly
            const quantityComponent = input.closest('quantity-input');
            if (quantityComponent) {
              // Force validation/update of the quantity component
              if (typeof quantityComponent.validateQtyRules === 'function') {
                quantityComponent.validateQtyRules();
              }
              
              // Dispatch a custom event that other components might be listening for
              quantityComponent.dispatchEvent(new CustomEvent('quantity:update', {
                bubbles: true,
                detail: { quantity: quantity }
              }));
            }
          });
        }
        
        // Update the savings badge with the actual quantity
        this.updateSavingsBadge(quantity);
        
        // Re-add our quantity change listeners after a short delay
        setTimeout(() => {
          this.initQuantityInputListeners();
        }, 100);
      }
    }
  );
}